import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, Mail, RefreshCw, Trash2, Copy, CheckCircle, Clock, XCircle } from 'lucide-react';
import { ConfirmDialog } from '@/components/organization/ConfirmDialog';
import {
  SiteInvite,
  listSiteInvites,
  createSiteInvite,
  revokeSiteInvite,
  resendSiteInvite,
} from '@/services/orgTeamApi';

export function SiteInvitesTab() {
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [invites, setInvites] = useState<SiteInvite[]>([]);

  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Action states
  const [resendingInvite, setResendingInvite] = useState<string | null>(null);
  const [revokingInvite, setRevokingInvite] = useState<SiteInvite | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  // Load invites
  const loadInvites = useCallback(async () => {
    try {
      const data = await listSiteInvites();
      setInvites(data);
    } catch (error) {
      console.error('Failed to load site invites:', error);
      toast({
        title: 'Error',
        description: 'Failed to load platform invitations',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  // Handle create invite
  const handleCreate = async () => {
    if (!createEmail.trim()) {
      toast({
        title: 'Error',
        description: 'Email is required',
        variant: 'destructive',
      });
      return;
    }

    setIsCreating(true);
    try {
      await createSiteInvite(createEmail.trim());

      toast({
        title: 'Invitation sent',
        description: `A platform invitation has been sent to ${createEmail}`,
      });

      setShowCreateDialog(false);
      setCreateEmail('');
      loadInvites();
    } catch (error: any) {
      console.error('Failed to create site invite:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send invitation',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Handle resend invite
  const handleResend = async (invite: SiteInvite) => {
    setResendingInvite(invite.id);
    try {
      await resendSiteInvite(invite.id);

      toast({
        title: 'Invitation resent',
        description: `A new invitation email has been sent to ${invite.email}`,
      });

      loadInvites();
    } catch (error: any) {
      console.error('Failed to resend invite:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to resend invitation',
        variant: 'destructive',
      });
    } finally {
      setResendingInvite(null);
    }
  };

  // Handle revoke invite
  const handleRevoke = async () => {
    if (!revokingInvite) return;

    setIsRevoking(true);
    try {
      await revokeSiteInvite(revokingInvite.id);

      toast({
        title: 'Invitation revoked',
        description: `The invitation for ${revokingInvite.email} has been revoked`,
      });

      setRevokingInvite(null);
      loadInvites();
    } catch (error: any) {
      console.error('Failed to revoke invite:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to revoke invitation',
        variant: 'destructive',
      });
    } finally {
      setIsRevoking(false);
    }
  };

  // Copy invite link
  const copyInviteLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({
      title: 'Copied',
      description: 'Invite link copied to clipboard',
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-3 w-3" />;
      case 'accepted':
        return <CheckCircle className="h-3 w-3" />;
      case 'expired':
        return <XCircle className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'outline';
      case 'accepted':
        return 'default';
      case 'expired':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Platform Invitations</CardTitle>
            <CardDescription>
              Invite new users to join the platform. They can create their own organization after signing up.
            </CardDescription>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Invite User
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : invites.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Mail className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No platform invitations</p>
            <Button variant="outline" className="mt-4" onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Send your first invitation
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Invited By</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="w-32">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invites.map((invite) => (
                <TableRow key={invite.id}>
                  <TableCell className="font-medium">{invite.email}</TableCell>
                  <TableCell>
                    <Badge
                      variant={getStatusBadgeVariant(invite.status) as any}
                      className="capitalize gap-1"
                    >
                      {getStatusIcon(invite.status)}
                      {invite.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{invite.invited_by_name || '-'}</TableCell>
                  <TableCell>{formatDate(invite.created_at)}</TableCell>
                  <TableCell className={isExpired(invite.expires_at) ? 'text-destructive' : ''}>
                    {formatDate(invite.expires_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyInviteLink(invite.invite_url)}
                        title="Copy invite link"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      {invite.status === 'pending' && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleResend(invite)}
                            disabled={resendingInvite === invite.id}
                            title="Resend invitation"
                          >
                            {resendingInvite === invite.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setRevokingInvite(invite)}
                            title="Revoke invitation"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Create Invite Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite to Platform</DialogTitle>
            <DialogDescription>
              Send a platform invitation. The user will be able to create their own organization after signing up.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="site-invite-email">Email Address</Label>
              <Input
                id="site-invite-email"
                type="email"
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <ConfirmDialog
        open={!!revokingInvite}
        onOpenChange={(open) => !open && setRevokingInvite(null)}
        title="Revoke Invitation"
        description={`Are you sure you want to revoke the invitation for "${revokingInvite?.email}"? They will no longer be able to use this invitation link.`}
        confirmLabel="Revoke"
        variant="destructive"
        isLoading={isRevoking}
        onConfirm={handleRevoke}
      />
    </Card>
  );
}
